import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Icon from '../../../components/AppIcon';

const AustraliaSection = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [activeCity, setActiveCity] = useState(() => localStorage.getItem('selectedCity') || null);

  useEffect(() => {
    const stored = localStorage.getItem('selectedCity');
    setActiveCity(stored);
  }, [location]);

  const ausFeatures = [
    {
      id: 1,
      icon: 'Building2',
      title: 'Sydney & NSW Market Intelligence',
      description: 'Real-time AI analysis of Sydney metro, Hunter Valley, Central Coast, and regional NSW — median house price $1.18M in 2025, up 6.2% YoY. Covers all suburbs from Bondi to Parramatta.',
      searchQuery: 'Sydney'
    },
    {
      id: 2,
      icon: 'TrendingUp',
      title: 'Melbourne & VIC Growth Insights',
      description: 'Deep market data for Melbourne CBD, inner suburbs, Mornington Peninsula, and regional Victoria. Median $920K in 2025 with strong rental yield recovery post-2024 rate stabilisation.',
      searchQuery: 'Melbourne'
    },
    {
      id: 3,
      icon: 'Sun',
      title: 'Brisbane & QLD Boom Tracker',
      description: 'Brisbane leads national growth at +11.4% YoY (2025), fuelled by post-Olympics infrastructure. Covers Gold Coast, Sunshine Coast, Cairns, and all QLD regional markets.',
      searchQuery: 'Brisbane'
    },
    {
      id: 4,
      icon: 'Zap',
      title: 'Perth & WA Hotspot Analysis',
      description: "Perth is Australia's top performer at +19.6% YoY (2025), driven by mining sector demand. Full coverage of WA metro, Fremantle, Joondalup, and regional mining towns.",
      searchQuery: 'Perth'
    },
    {
      id: 5,
      icon: 'MapPin',
      title: 'Adelaide, SA & NT Coverage',
      description: 'Adelaide median $740K with +13.1% growth (2025) — most affordable major capital. Plus Darwin NT coverage ($510K median) with unique tropical market dynamics and mining-linked demand.',
      searchQuery: 'Adelaide'
    },
    {
      id: 6,
      icon: 'Globe',
      title: 'Canberra, ACT & TAS Markets',
      description: 'Canberra ACT median $850K with government-sector stability. Hobart TAS $620K with strong short-term rental yields. Full coverage of all territories and island markets.',
      searchQuery: 'Canberra'
    }
  ];

  const ausStats = [
    { value: '$1.02M', label: 'National Median (2025)' },
    { value: '8', label: 'States & Territories' },
    { value: '+8.3%', label: 'Avg National Growth 2024' },
    { value: '100%', label: 'AU Coverage' }
  ];

  return (
    <section id="australia" className="py-12 md:py-20 lg:py-28 relative overflow-hidden" style={{ background: 'transparent' }}>
      {/* Background glow */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 right-0 w-96 h-96 rounded-full" style={{
          background: 'radial-gradient(circle, rgba(0, 212, 255, 0.06) 0%, transparent 70%)',
          filter: 'blur(60px)'
        }} />
        <div className="absolute bottom-0 left-0 w-80 h-80 rounded-full" style={{
          background: 'radial-gradient(circle, rgba(0, 255, 136, 0.06) 0%, transparent 70%)',
          filter: 'blur(60px)'
        }} />
      </div>
      <div className="relative max-w-7xl mx-auto px-4 md:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12 md:mb-16">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm md:text-base font-medium mb-4" style={{
            background: 'rgba(0, 212, 255, 0.1)',
            border: '1px solid rgba(0, 212, 255, 0.3)',
            color: '#00D4FF'
          }}>
            <span>🇦🇺</span>
            <span>AUSTRALIA MARKET — 100% COVERAGE</span>
          </div>
          <h2 className="text-2xl md:text-3xl lg:text-4xl xl:text-5xl font-bold mb-4 md:mb-6" style={{
            fontFamily: "'Orbitron', sans-serif"
          }}>
            <span style={{ color: '#00D4FF', textShadow: '0 0 30px rgba(0, 212, 255, 0.4)' }}>Built Specifically for</span>
            <span style={{ color: '#E8F4FF' }}> Australian Property Market</span>
          </h2>
          <p className="text-base md:text-lg lg:text-xl max-w-3xl mx-auto" style={{ color: '#6B9EC4' }}>
            ListingLens delivers AI-powered property intelligence across all 8 Australian states and territories — from Sydney's premium suburbs to Darwin's tropical market. Powered by 2024/2026 CoreLogic, PropTrack, and Domain data.
          </p>
        </div>

        {/* Australia Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6 mb-12 md:mb-16">
          {ausStats?.map((stat, idx) => (
            <div key={idx} className="text-center p-4 md:p-6 rounded-xl" style={{
              background: 'rgba(10, 22, 40, 0.8)',
              border: '1px solid rgba(0, 212, 255, 0.15)'
            }}>
              <div className="text-2xl md:text-3xl font-bold mb-1" style={{
                fontFamily: "'JetBrains Mono', monospace",
                color: '#00D4FF',
                textShadow: '0 0 15px rgba(0, 212, 255, 0.4)'
              }}>{stat?.value}</div>
              <div className="text-xs md:text-sm" style={{ color: '#6B9EC4' }}>{stat?.label}</div>
            </div>
          ))}
        </div>

        {/* Features Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
          {ausFeatures?.map((feature) => (
            <button
              key={feature?.id}
              type="button"
              onClick={() => {
                localStorage.setItem('selectedCity', feature?.searchQuery);
                setActiveCity(feature?.searchQuery);
                navigate(`/search-results?location=${feature?.searchQuery}&mode=buy&region=australia`);
              }}
              className="p-6 md:p-8 rounded-xl transition-all duration-300 group text-left w-full cursor-pointer"
              style={{
                background: 'rgba(10, 22, 40, 0.8)',
                border: '1px solid rgba(0, 212, 255, 0.15)'
              }}
              onMouseEnter={e => {
                e.currentTarget.style.border = '1px solid rgba(0, 212, 255, 0.4)';
                e.currentTarget.style.boxShadow = '0 0 18px rgba(0, 212, 255, 0.12)';
              }}
              onMouseLeave={e => {
                e.currentTarget.style.border = '1px solid rgba(0, 212, 255, 0.15)';
                e.currentTarget.style.boxShadow = 'none';
              }}
            >
              <div className="w-12 h-12 md:w-14 md:h-14 rounded-xl flex items-center justify-center mb-4 md:mb-6" style={{
                background: 'rgba(0, 212, 255, 0.1)',
                border: '1px solid rgba(0, 212, 255, 0.2)'
              }}>
                <Icon name={feature?.icon} size={24} color="#00D4FF" />
              </div>
              <h3 className="text-base md:text-lg font-semibold mb-2 md:mb-3" style={{ color: '#E8F4FF' }}>
                {feature?.title}
              </h3>
              <p className="text-sm md:text-base leading-relaxed" style={{ color: '#6B9EC4' }}>
                {feature?.description}
              </p>
              <div className="mt-4 flex items-center gap-1" style={{ color: '#00D4FF', fontSize: '13px', opacity: 0.8 }}>
                <span>Explore {feature?.searchQuery} properties</span>
                <Icon name="ArrowRight" size={13} color="#00D4FF" />
              </div>
            </button>
          ))}
        </div>

        {/* Full Coverage CTA */}
        <div className="text-center mt-12 md:mt-16">
          <div className="flex items-center justify-center gap-2 mb-4">
            <Icon name="MapPin" size={18} color="#00D4FF" />
            <span className="text-sm md:text-base font-medium" style={{ color: '#6B9EC4' }}>100% Coverage Across Australia</span>
          </div>
          <div className="inline-flex flex-wrap items-center justify-center gap-2 mb-3">
            {['Sydney', 'Melbourne', 'Brisbane', 'Perth', 'Adelaide', 'Canberra', 'Hobart', 'Darwin']?.map((city) => {
              const isActive = activeCity === city;
              return (
              <button
                key={city}
                type="button"
                onClick={() => {
                  localStorage.setItem('selectedCity', city);
                  setActiveCity(city);
                  navigate(`/search-results?location=${city}&mode=buy&region=australia`);
                }}
                className="px-4 py-2 rounded-lg text-sm font-medium cursor-pointer transition-all duration-200 relative"
                style={isActive ? {
                  background: 'rgba(0, 212, 255, 0.25)',
                  border: '1px solid rgba(0, 212, 255, 0.8)',
                  color: '#00D4FF',
                  boxShadow: '0 0 16px rgba(0, 212, 255, 0.45), inset 0 0 8px rgba(0, 212, 255, 0.1)'
                } : {
                  background: 'rgba(0, 212, 255, 0.1)',
                  border: '1px solid rgba(0, 212, 255, 0.3)',
                  color: '#00D4FF'
                }}
                onMouseEnter={e => {
                  if (!isActive) {
                    e.currentTarget.style.background = 'rgba(0, 212, 255, 0.2)';
                    e.currentTarget.style.border = '1px solid rgba(0, 212, 255, 0.6)';
                    e.currentTarget.style.boxShadow = '0 0 12px rgba(0, 212, 255, 0.3)';
                  }
                }}
                onMouseLeave={e => {
                  if (!isActive) {
                    e.currentTarget.style.background = 'rgba(0, 212, 255, 0.1)';
                    e.currentTarget.style.border = '1px solid rgba(0, 212, 255, 0.3)';
                    e.currentTarget.style.boxShadow = 'none';
                  }
                }}
              >
                {isActive && (
                  <span className="absolute -top-1.5 -right-1.5 w-3 h-3 rounded-full flex items-center justify-center" style={{
                    background: '#00D4FF',
                    boxShadow: '0 0 6px rgba(0, 212, 255, 0.8)'
                  }} />
                )}
                {city}
              </button>
              );
            })}
          </div>
          <p className="mt-1 text-xs" style={{ color: '#4A7A9B' }}>Covering NSW · VIC · QLD · WA · SA · ACT · TAS · NT — Every state and territory</p>
          <div className="mt-6 flex flex-wrap items-center justify-center gap-4">
            <button
              type="button"
              onClick={() => navigate('/dashboard')}
              className="px-8 py-3 rounded-xl font-semibold text-sm md:text-base cursor-pointer transition-all duration-200"
              style={{
                background: 'linear-gradient(135deg, #00D4FF, #0099CC)',
                color: '#000000',
                boxShadow: '0 0 20px rgba(0, 212, 255, 0.4)'
              }}
              onMouseEnter={e => {
                e.currentTarget.style.boxShadow = '0 0 30px rgba(0, 212, 255, 0.6)';
                e.currentTarget.style.transform = 'translateY(-1px)';
              }}
              onMouseLeave={e => {
                e.currentTarget.style.boxShadow = '0 0 20px rgba(0, 212, 255, 0.4)';
                e.currentTarget.style.transform = 'translateY(0)';
              }}
            >
              Start Analysing Properties
            </button>
            <button
              type="button"
              onClick={() => navigate('/list-your-property-landing')}
              className="px-8 py-3 rounded-xl font-semibold text-sm md:text-base cursor-pointer transition-all duration-200"
              style={{
                background: 'transparent',
                border: '2px solid rgba(0, 255, 136, 0.6)',
                color: '#00FF88',
                boxShadow: '0 0 16px rgba(0, 255, 136, 0.2)'
              }}
              onMouseEnter={e => {
                e.currentTarget.style.background = 'rgba(0, 255, 136, 0.1)';
                e.currentTarget.style.boxShadow = '0 0 24px rgba(0, 255, 136, 0.4)';
                e.currentTarget.style.transform = 'translateY(-1px)';
              }}
              onMouseLeave={e => {
                e.currentTarget.style.background = 'transparent';
                e.currentTarget.style.boxShadow = '0 0 16px rgba(0, 255, 136, 0.2)';
                e.currentTarget.style.transform = 'translateY(0)';
              }}
            >
              Sell Your Property →
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AustraliaSection;
